import { Card } from "@contractor-quoting/shared-ui";
import { Download } from "lucide-react";

const sections = [
  {
    title: "1. Choose a canonical job type",
    body: "Choose the narrowest platform job identity that has one coherent measurement and pricing method—for example painting.interior_room rather than painting. Job types define stable field and metric keys; they never contain your prices.",
  },
  {
    title: "2. Define purposeful fields",
    body: "Give every field a purpose: measurement, pricing, scope, feasibility, customer preference, contractor fulfillment, or manual review. Then set readiness separately: required for price, submission, contractor approval, or optional. A price-required field must feed a measurement, price, eligibility, or blocking review rule.",
  },
  {
    title: "3. Offer measurement methods",
    body: "Customers may know total area, room dimensions, equal-wall dimensions, or individual wall segments. List the inputs each method needs and route every supported method to the same canonical derived metric. Never require a customer to calculate a total the runbook can derive.",
  },
  {
    title: "4. Build derived metrics",
    body: "Reference registered calculation handlers and explicit dependencies. Painting may derive gross wall area, net wall area, ceiling area, paintable area, additional-coat area, material quantity, and high-wall flags. Imported formulas or executable code are not allowed.",
  },
  {
    title: "5. Reference shared rate codes",
    body: "The runbook Pricing Recipe links a component to a stable contractor-wide rate code and a field or metric quantity. Shared rates are the reusable-money section of runbook configuration. Conditions decide whether a component applies; explanations describe it without exposing confidential cost or margin.",
  },
  {
    title: "6. Scope adjustments",
    body: "Mark measurement allowance only on eligible measured quantities, material waste only on relevant materials, markup only on cost-basis items, contingency only where intended, and tax through the correct tax class. Sell prices and fixed packages must not be marked up twice.",
  },
  {
    title: "7. Configure price presentation",
    body: "New production runbooks use one deterministic amount. Range and starting-at modes must be explicitly selected. Customer rounding changes display only—the exact minor-unit amount remains in the calculation snapshot.",
  },
  {
    title: "8. Defaults, review, and images",
    body: "A default must be contractor-configured and shown as an assumption. Review rules may block automated pricing only when intentional. Image guidance should say when an image helps and whether it blocks submission; images remain private attachments and are not treated as observed facts unless analysis actually runs.",
  },
  {
    title: "9. Test before publishing",
    body: "Add deterministic test cases with inputs, expected metrics, components, readiness, review flags, and an expected formula or amount when test rates are supplied. Publishing checks duplicate keys, missing dependencies and handlers, cycles, unknown rates, incompatible units, orphaned blockers, blanket adjustment misuse, missing tests, and fixed recipes that collect unused measurements.",
  },
  {
    title: "10. Understand version pinning",
    body: "Publishing creates an immutable version. New projects use the active job definition and shared-rate versions as one runbook configuration. Generated estimates pin their input revision, runbook IDs, exact rate snapshots, quantities, rules, line items, adjustments, taxes, and totals; later changes do not rewrite an existing quote.",
  },
] as const;

export function ContractorDocsPage() {
  return (
    <div className="mx-auto max-w-5xl">
      <p className="text-sm font-bold uppercase tracking-widest text-emerald-800">
        Contractor documentation
      </p>
      <h1 className="mt-2 text-4xl font-black">Job runbook authoring</h1>
      <p className="mt-3 max-w-3xl text-stone-600">
        A job runbook defines what Mezoniq collects, derives, reviews, and prices for one canonical
        job type. Open a built-in baseline in Quoting setup, customize it in the browser, and keep
        reusable monetary values in Shared pricing.
      </p>

      <Card className="mt-7 border-amber-200 bg-amber-50">
        <h2 className="text-xl font-black">Painting example</h2>
        <p className="mt-2 text-sm leading-6 text-stone-700">
          The downloadable runbook is a complete interior-room painting example with no contractor
          dollar values. It supports direct wall area, room dimensions, equal walls, individual
          segments, openings, ceilings, preparation, coats, paint supply and tier, high-wall review,
          attachments, explanations, and a 4 × 12 ft × 14 ft = 672 sq ft test. Add your
          contractor-owned values through the shared-rates section before publishing.
        </p>
        <a
          className="mt-4 inline-flex min-h-11 items-center gap-2 rounded-xl bg-emerald-900 px-4 text-sm font-black text-white hover:bg-emerald-800"
          href="/api/v1/contractor/runbooks/painting.interior_room/export"
        >
          <Download size={17} /> Download painting runbook (.xlsx)
        </a>
      </Card>

      <div className="mt-6 grid gap-4 md:grid-cols-2">
        {sections.map((section) => (
          <Card key={section.title}>
            <h2 className="text-lg font-black">{section.title}</h2>
            <p className="mt-2 text-sm leading-6 text-stone-600">{section.body}</p>
          </Card>
        ))}
      </div>

      <Card className="mt-6">
        <h2 className="text-xl font-black">Runbook sections</h2>
        <div className="mt-3 grid gap-5 text-sm md:grid-cols-2">
          <div>
            <h3 className="font-black">Job definition</h3>
            <p className="mt-1 text-stone-600">
              Settings, Fields, Question Rules, Measurement Methods, Derived Metrics, Pricing
              Recipe, Defaults, Review Rules, Attachment Guidance, Customer Explanations, and Test
              Cases.
            </p>
          </div>
          <div>
            <h3 className="font-black">Shared rates</h3>
            <p className="mt-1 text-stone-600">
              Settings, Rates, Adjustment Policies, Tax Classes, and Effective Dates. Replace all
              example values with your own tested configuration before publishing.
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
}
